/**
 * BillingSuccess - Strona sukcesu po płatności Stripe
 * /panel/billing/success?session_id=xxx
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, XCircle, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import confetti from 'canvas-confetti';

import { API_BASE as API_URL } from '@/utils/api';
import { useTranslation } from 'react-i18next';

const BillingSuccess = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [planId, setPlanId] = useState(null);
  const [attempts, setAttempts] = useState(0);
  
  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);
  
  useEffect(() => {
    if (status === 'success') {
      // Trigger confetti
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    }
  }, [status]);
  
  const checkPaymentStatus = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/billing/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.payment_status === 'paid') {
          setPlanId(data.plan_id);
          setStatus('success');
          return;
        } else if (data.status === 'expired') {
          setStatus('error');
          return;
        }
        
        // If still pending, poll again (max 5 attempts)
        if (attempts < 5) {
          setTimeout(() => {
            setAttempts(prev => prev + 1);
            checkPaymentStatus();
          }, 2000);
        } else {
          setStatus('error');
        }
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      if (attempts < 5) {
        setTimeout(() => {
          setAttempts(prev => prev + 1);
          checkPaymentStatus();
        }, 2000);
      } else {
        setStatus('error');
      }
    }
  };
  
  const getPlanName = (id) => {
    const names = { starter: 'START', pro: 'PRO', agency: 'AGENCJA' };
    return names[id] || id;
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-emerald-50/30 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
      >
        {status === 'checking' && (
          <>
            <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-zinc-900 mb-2">
              Sprawdzamy płatność...
            </h1>
            <p className="text-zinc-600">
              To może potrwać kilka sekund
            </p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', damping: 10 }}
              className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle className="w-10 h-10 text-emerald-500" />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h1 className="text-2xl font-bold text-zinc-900 mb-2">
                Płatność zakończona! 🎉
              </h1>
              <p className="text-zinc-600 mb-6">
                Plan <span className="font-semibold text-emerald-600">{getPlanName(planId)}</span> został aktywowany.
                Masz teraz pełny dostęp do wszystkich funkcji.
              </p>
              
              <div className="bg-emerald-50 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-center gap-2 text-emerald-700">
                  <Sparkles className="w-5 h-5" />
                  <span className="font-medium">Wszystko gotowe!</span>
                </div>
              </div>
              
              <Button
                onClick={() => navigate('/panel')}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2"
              >
                Przejdź do panelu
                <ArrowRight className="w-4 h-4" />
              </Button>
            </motion.div>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-zinc-900 mb-2">
              Coś poszło nie tak
            </h1>
            <p className="text-zinc-600 mb-6">
              Nie udało się potwierdzić płatności. Spróbuj ponownie lub skontaktuj się z nami.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => navigate('/panel/cennik')}
                variant="outline"
                className="flex-1 py-3 rounded-xl"
              >
                Spróbuj ponownie
              </Button>
              <Button
                onClick={() => navigate('/panel')}
                className="flex-1 bg-gray-900 text-white py-3 rounded-xl"
              >
                Wróć do panelu
              </Button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default BillingSuccess;
